import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  Stack,
  Link,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  InputAdornment,
  IconButton,
  Chip,
  alpha,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  ArrowBack,
  CheckCircle,
  Star,
  Security,
  Speed,
  TrendingUp,
  Rocket,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { useAuth } from '../../context/AuthContext';

// Animations
const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(5deg); }
  66% { transform: translateY(10px) rotate(-5deg); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
  50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.5); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const AuthenticationPageNew = ({ onLoginSuccess, onBack, initialMode = 'login' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mounted, setMounted] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.email || !formData.password) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      const payload = await login(formData.email, formData.password);
      setSuccess('Login successful! Redirecting...');
      setLoading(false);
      setTimeout(() => {
        onLoginSuccess(payload?.user || { email: formData.email });
      }, 600);
    } catch (err) {
      setError(err?.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.email || !formData.password || !formData.confirmPassword || !formData.name) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        setLoading(false);
        return;
      }

      // Enforce password complexity
      const hasUppercase = /[A-Z]/.test(formData.password);
      const hasLowercase = /[a-z]/.test(formData.password);
      const hasNumber = /[0-9]/.test(formData.password);
      const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password);
      if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
        setError('Password must include uppercase, lowercase, number, and special character');
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      // Call register API
      const payload = await register(formData.email, formData.password, formData.name);
      setSuccess('Account created successfully! Redirecting...');
      setLoading(false);
      setTimeout(() => {
        onLoginSuccess(payload?.user || { email: formData.email, name: formData.name });
      }, 600);
    } catch (err) {
      setError(err?.message || 'Signup failed. Please try again.');
      setLoading(false);
    }
  };

  const features = [
    { icon: <Rocket />, text: 'FMCSA Verified Carrier Database', color: '#10b981' },
    { icon: <Security />, text: 'Bank-level security & encryption', color: '#3b82f6' },
    { icon: <Speed />, text: 'Email campaigns in minutes', color: '#8b5cf6' },
    { icon: <TrendingUp />, text: 'Real-time analytics & tracking', color: '#06b6d4' },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: isDark
          ? 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
        backgroundSize: '400% 400%',
        animation: `${gradientShift} 15s ease infinite`,
        display: 'flex',
        alignItems: 'center',
        py: { xs: 4, md: 6 },
        position: 'relative',
        overflow: 'hidden',
        fontFamily: '"Sora", "Inter", sans-serif',
      }}
    >
      {/* Animated Background Elements */}
      {[...Array(15)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: { xs: 60, md: 100 },
            height: { xs: 60, md: 100 },
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(
              i % 3 === 0 ? '#3b82f6' : i % 3 === 1 ? '#8b5cf6' : '#ec4899',
              0.1
            )}, transparent)`,
            top: `${(i * 17) % 100}%`,
            left: `${(i * 23) % 100}%`,
            animation: `${floatAnimation} ${6 + (i % 3) * 2}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
          }}
        />
      ))}

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Fade in timeout={500}>
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{
              color: isDark ? '#f1f5f9' : '#1e3a8a',
              mb: 3,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              background: isDark ? alpha('#1e293b', 0.4) : alpha('#ffffff', 0.8),
              backdropFilter: 'blur(10px)',
              px: 3,
              py: 1.5,
              borderRadius: 3,
              border: `1px solid ${alpha(isDark ? '#334155' : '#3b82f6', 0.2)}`,
              '&:hover': {
                background: isDark ? alpha('#1e293b', 0.6) : alpha('#ffffff', 0.95),
                transform: 'translateX(-5px)',
              },
              transition: 'all 0.3s',
            }}
          >
            Back to Home
          </Button>
        </Fade>

        <Zoom in timeout={700}>
          <Card
            sx={{
              borderRadius: { xs: 3, md: 5 },
              overflow: 'hidden',
              boxShadow: isDark
                ? '0 25px 80px rgba(0, 0, 0, 0.5)'
                : '0 25px 80px rgba(15, 23, 42, 0.15)',
              border: `1px solid ${alpha(isDark ? '#334155' : '#3b82f6', 0.1)}`,
              background: isDark
                ? alpha('#1e293b', 0.6)
                : alpha('#ffffff', 0.95),
              backdropFilter: 'blur(20px)',
              animation: mounted ? `${pulseGlow} 3s ease-in-out infinite` : 'none',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                minHeight: { md: '600px' },
              }}
            >
              {/* Left Side - Features Showcase */}
              <Box
                sx={{
                  p: { xs: 4, md: 6 },
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)',
                  backgroundSize: '200% 200%',
                  animation: `${gradientShift} 8s ease infinite`,
                  color: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 4,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Decorative Shimmer Effect */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background:
                      'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                    backgroundSize: '1000px 100%',
                    animation: `${shimmer} 3s infinite linear`,
                  }}
                />

                <Fade in timeout={1000}>
                  <Stack spacing={2}>
                    <Chip
                      label="🚀 Carrier Finder Pro"
                      sx={{
                        background: alpha('#ffffff', 0.2),
                        color: '#fff',
                        fontWeight: 700,
                        alignSelf: 'flex-start',
                        px: 2,
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha('#ffffff', 0.3)}`,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: '"Sora", "Inter", sans-serif',
                        fontWeight: 900,
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        lineHeight: 1.2,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      FMCSA Carrier Search + Email Automation
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        color: alpha('#ffffff', 0.9),
                        lineHeight: 1.8,
                      }}
                    >
                      Access FMCSA verified carrier database, powerful email automation, and real-time analytics. Join thousands of professionals transforming their carrier outreach.
                    </Typography>
                  </Stack>
                </Fade>

                <Stack spacing={2.5}>
                  {features.map((feature, idx) => (
                    <Fade in timeout={1200 + idx * 200} key={idx}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{
                          background: alpha('#ffffff', 0.1),
                          backdropFilter: 'blur(10px)',
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${alpha('#ffffff', 0.2)}`,
                          transition: 'all 0.3s',
                          '&:hover': {
                            background: alpha('#ffffff', 0.15),
                            transform: 'translateX(10px)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            background: alpha('#ffffff', 0.2),
                            borderRadius: '50%',
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {React.cloneElement(feature.icon, { sx: { fontSize: 24 } })}
                        </Box>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          {feature.text}
                        </Typography>
                      </Stack>
                    </Fade>
                  ))}
                </Stack>

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Chip
                    icon={<CheckCircle sx={{ color: '#10b981 !important' }} />}
                    label="FMCSA Verified"
                    size="small"
                    sx={{
                      background: alpha('#10b981', 0.25),
                      color: '#fff',
                      fontWeight: 700,
                      backdropFilter: 'blur(10px)',
                      border: `1px solid ${alpha('#10b981', 0.4)}`,
                    }}
                  />
                  <Chip
                    icon={<Star sx={{ color: '#fbbf24 !important' }} />}
                    label="15K+ Carriers"
                    size="small"
                    sx={{
                      background: alpha('#ffffff', 0.15),
                      color: '#fff',
                      fontWeight: 600,
                      backdropFilter: 'blur(10px)',
                    }}
                  />
                  <Chip
                    label="Real-time Data"
                    size="small"
                    sx={{
                      background: alpha('#ffffff', 0.15),
                      color: '#fff',
                      fontWeight: 600,
                      backdropFilter: 'blur(10px)',
                    }}
                  />
                </Stack>
              </Box>

              {/* Right Side - Login Form */}
              <Box sx={{ p: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {error && (
                  <Zoom in>
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'error.main',
                      }}
                    >
                      {error}
                    </Alert>
                  </Zoom>
                )}
                {success && (
                  <Zoom in>
                    <Alert
                      severity="success"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'success.main',
                      }}
                    >
                      {success}
                    </Alert>
                  </Zoom>
                )}

                <Stack spacing={3} component="form" onSubmit={isLogin ? handleLogin : handleSignup}>
                  <Fade in timeout={800}>
                    <Stack spacing={1}>
                      <Typography
                        sx={{
                          fontFamily: '"Sora", "Inter", sans-serif',
                          fontWeight: 800,
                          fontSize: { xs: '1.75rem', md: '2rem' },
                          color: isDark ? '#f1f5f9' : '#0f172a',
                          background: isDark
                            ? 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)'
                            : 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {isLogin ? 'Sign In' : 'Create Account'}
                      </Typography>
                      <Typography
                        sx={{
                          color: isDark ? alpha('#f1f5f9', 0.7) : '#64748b',
                          fontSize: '1rem',
                        }}
                      >
                        {isLogin
                          ? 'Welcome back! Enter your credentials'
                          : 'Start your journey with Carrier Finder Pro'}
                      </Typography>
                    </Stack>
                  </Fade>

                  {!isLogin && (
                    <Fade in timeout={1000}>
                      <TextField
                        fullWidth
                        name="name"
                        label="Full Name"
                        placeholder="John Doe"
                        variant="outlined"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={loading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            background: isDark ? alpha('#1e293b', 0.4) : alpha('#f8fafc', 0.8),
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.3s',
                            '&:hover': {
                              background: isDark ? alpha('#1e293b', 0.6) : alpha('#f8fafc', 1),
                            },
                            '&.Mui-focused': {
                              boxShadow: `0 0 0 3px ${alpha('#3b82f6', 0.1)}`,
                            },
                          },
                        }}
                      />
                    </Fade>
                  )}

                  <Fade in timeout={1200}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email Address"
                      type="email"
                      placeholder="you@company.com"
                      variant="outlined"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email sx={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: isDark ? alpha('#1e293b', 0.4) : alpha('#f8fafc', 0.8),
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s',
                          '&:hover': {
                            background: isDark ? alpha('#1e293b', 0.6) : alpha('#f8fafc', 1),
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#3b82f6', 0.1)}`,
                          },
                        },
                      }}
                    />
                  </Fade>

                  <Fade in timeout={1400}>
                    <TextField
                      fullWidth
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      variant="outlined"
                      value={formData.password}
                      onChange={handleInputChange}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={loading}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: isDark ? alpha('#1e293b', 0.4) : alpha('#f8fafc', 0.8),
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s',
                          '&:hover': {
                            background: isDark ? alpha('#1e293b', 0.6) : alpha('#f8fafc', 1),
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#3b82f6', 0.1)}`,
                          },
                        },
                      }}
                    />
                  </Fade>

                  {!isLogin && (
                    <Fade in timeout={1600}>
                      <TextField
                        fullWidth
                        name="confirmPassword"
                        label="Confirm Password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        variant="outlined"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        disabled={loading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            background: isDark ? alpha('#1e293b', 0.4) : alpha('#f8fafc', 0.8),
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.3s',
                            '&:hover': {
                              background: isDark ? alpha('#1e293b', 0.6) : alpha('#f8fafc', 1),
                            },
                            '&.Mui-focused': {
                              boxShadow: `0 0 0 3px ${alpha('#3b82f6', 0.1)}`,
                            },
                          },
                        }}
                      />
                    </Fade>
                  )}

                  <Zoom in timeout={1800}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{
                        py: 1.8,
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        backgroundSize: '200% 200%',
                        animation: `${gradientShift} 5s ease infinite`,
                        boxShadow: '0 10px 30px rgba(59, 130, 246, 0.3)',
                        transition: 'all 0.3s',
                        '&:hover': {
                          boxShadow: '0 15px 40px rgba(59, 130, 246, 0.4)',
                          transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                          opacity: 0.7,
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={28} sx={{ color: '#fff' }} />
                      ) : isLogin ? (
                        'Sign In to Dashboard'
                      ) : (
                        'Create Your Account'
                      )}
                    </Button>
                  </Zoom>
                </Stack>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: isDark ? alpha('#f1f5f9', 0.7) : '#64748b', fontSize: '0.95rem' }}
                  >
                    {isLogin ? "Don't have an account? " : 'Already have an account? '}
                    <Link
                      component="button"
                      variant="body2"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsLogin(!isLogin);
                        setFormData({
                          email: '',
                          password: '',
                          confirmPassword: '',
                          name: '',
                        });
                        setError('');
                      }}
                      sx={{
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {isLogin ? 'Create one now' : 'Sign in here'}
                    </Link>
                  </Typography>
                </Box>

                {!isMobile && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 3,
                      color: isDark ? alpha('#f1f5f9', 0.5) : '#94a3b8',
                      borderTop: `1px solid ${alpha(isDark ? '#334155' : '#cbd5e1', 0.5)}`,
                      pt: 2,
                      fontSize: '0.85rem',
                    }}
                  >
                    🔒 Secured with 256-bit encryption · Your data is safe with us
                  </Typography>
                )}
              </Box>
            </Box>
          </Card>
        </Zoom>
      </Container>
    </Box>
  );
};

export default AuthenticationPageNew;
